import { DataRow } from "@/service/entity/project/helpers/excel.helper";
import { GTIN, LONG_BULLETS, SHORT_BULLETS } from "@/app/(protected)/pdp-copy/components/GeneratedCopy/constants";
import { sanitizeCell } from "./spreadsheet-sanitizer.utils";

export const getInitials = (fullName: string): string => {
  if (!fullName) return "";
  const names = fullName.trim().split(" ");
  const initials = names.map((name) => name[0]?.toUpperCase()).join("");
  return initials.slice(0, 2);
};

export const capitalizeFirstChar = (input: string): string => {
  if (typeof input !== "string" || input.length === 0) return input;
  return input
    .split(" ")
    .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
    .join(" ");
};

// Function to convert a string to camelCase and replace "/" with "Or"
export function toCamelCase(str: string): string {
  return str
    .replaceAll(/[-_\s]+(.)?/g, (_, letter) => (letter ? letter.toUpperCase() : ""))
    .replaceAll("/", "Or")
    .replaceAll(/([.(])([a-z])/g, (_, __, letter) => letter.toUpperCase()) // Handles dots and parentheses followed by a letter
    .replaceAll(/[().]/g, "")
    .replace(/^(.)/, (match) => match.toLowerCase());
}

// Function to convert object keys to camelCase
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function convertToCamelCase(data: Record<string, any>): Record<string, any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const newData: Record<string, any> = {};
  Object.keys(data).forEach((key) => {
    const camelCasedKey = toCamelCase(key);
    newData[camelCasedKey] = data[key];
  });
  return newData;
}

export const getArrayFromCSVString = (val: string | undefined): string[] => {
  return val?.split(",").map((item) => item.trim()) || [];
};

// First letter uppercase and remove any non-letter character
export const cleanString = (value: string) => value[0]?.toUpperCase() + value?.slice(1)?.replaceAll(/[^a-zA-Z]/g, "");

export const removeSpaces = (input: string) => input?.trim()?.replaceAll(/\s+/g, "");

/**
 * Extracts a substring of a specified length from a given string without cutting off words.
 */
export const getSafeSubstring = (input: string, length: number): string => {
  if (length >= input.length) {
    return input;
  }
  const lastSpaceIndex = input.substring(0, length + 1).lastIndexOf(" ");

  if (lastSpaceIndex === -1 || lastSpaceIndex === length) {
    return input.substring(0, length);
  }

  return input.substring(0, lastSpaceIndex);
};

/**
 * Add separator between two strings
 */
export const joinStringWithSeparator = (separator: string, strings: string[]) => {
  const filteredStrings = strings.filter(Boolean);
  return filteredStrings.length > 1 ? filteredStrings.join(` ${separator} `) : filteredStrings.join("");
};

export const removeSpacesAroundDash = (str: string): string => {
  if (!str) return str;
  return str
    .split("-")
    .map((part) => part.trim())
    .join("-");
};

export const sanitizeForCSV = (input: string): string => {
  // Defense-in-depth: reuse the centralized formula-injection sanitizer so the
  // neutralization rule (dangerous lead chars, invisible-char handling,
  // idempotency) is consistent across ingest and export layers.
  const neutralized = sanitizeCell(input);
  // Wrap in quotes and escape embedded quotes for valid CSV.
  return `"${neutralized.replaceAll('"', '""')}"`;
};

type CsvDataRow = DataRow & {
  label: string;
  description: string | string[];
};

export const convertToCSV = (data: CsvDataRow[]): string => {
  // Generate header row
  const headerRow: string[] = ["Label"];
  const descriptionRow: string[] = ["Description"];
  data.forEach((item) => {
    const baseLabel =
      item.label === GTIN
        ? item.label
        : item.label
            .replaceAll(/([a-z0-9])([A-Z])/g, "$1 $2") // insert space before capital letters
            .replaceAll(/[_-]/g, " ") // replace underscores and hyphens with spaces
            .split(" ")
            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
    if (Array.isArray(item.description)) {
      item.description.forEach((desc, index) => {
        let labelWithIndex = `${baseLabel} ${index + 1}`;
        const isBullets = item.label === LONG_BULLETS || item.label === SHORT_BULLETS;
        if (labelWithIndex.toString().toLowerCase().includes("long")) {
          labelWithIndex = labelWithIndex.replaceAll(/long/gi, "");
        }
        if (labelWithIndex.toString().toLowerCase().includes("bullets")) {
          labelWithIndex = labelWithIndex.replaceAll(/bullets/gi, "Bullet");
        }

        let cleanedDesc = desc.trim();
        if (isBullets && cleanedDesc.endsWith(".")) {
          cleanedDesc = cleanedDesc.slice(0, -1);
        }

        headerRow.push(sanitizeForCSV(labelWithIndex));
        descriptionRow.push(sanitizeForCSV(cleanedDesc));
      });
    } else {
      const isBullets = item.label === LONG_BULLETS || item.label === SHORT_BULLETS;
      let cleanedDesc = typeof item.description === "string" ? item.description.trim() : String(item.description ?? "").trim();
      if (isBullets && cleanedDesc.endsWith(".")) {
        cleanedDesc = cleanedDesc.slice(0, -1);
      }

      headerRow.push(sanitizeForCSV(baseLabel));
      descriptionRow.push(sanitizeForCSV(cleanedDesc));
    }
  });

  // Add UTF-8 BOM to the beginning
  const csvContent = [headerRow.join(","), descriptionRow.join(",")].join("\n");
  return `\uFEFF${csvContent}`;
};

export const applyDefaultValue = (value: string, defalutValue: string) => {
  return value ?? defalutValue;
};

export const getDefaultStringValue = (value: string | null | undefined): string => value ?? "";

export const lowerCaseConversion = (str?: string) => str?.toLowerCase();

export const generateTrimmedId = (id: string): string => id?.replaceAll(/\s+/g, "-")?.toLowerCase();
